import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * PDF Generator for System Quotations
 * Generates professional quotations with F&H letterhead
 */

class PDFGenerator {
  /**
   * Generate quotation PDF
   */
  static generateQuotation(recommendation, userParams) {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    let yPos = 20;

    // ============ HEADER / LETTERHEAD ============
    doc.setFillColor(255, 107, 53); // #ff6b35
    doc.rect(0, 0, pageWidth, 40, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('F & H AUDIO SOLUTIONS LLP', pageWidth / 2, 18, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Professional PA System Consultants', pageWidth / 2, 26, { align: 'center' });
    doc.text('Lalapeta, Guntur - 522003 | Phone: 7287809847', pageWidth / 2, 33, { align: 'center' });

    yPos = 50;

    // ============ QUOTATION TITLE ============
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('PRELIMINARY SYSTEM QUOTATION', pageWidth / 2, yPos, { align: 'center' });

    yPos += 10;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const quoteId = `SYS-${Date.now().toString().slice(-6)}-2025`;
    const quoteDate = new Date().toLocaleDateString('en-IN');
    doc.text(`Quote #: ${quoteId}`, 15, yPos);
    doc.text(`Date: ${quoteDate}`, pageWidth - 15, yPos, { align: 'right' });

    yPos += 10;

    // ============ CLIENT REQUIREMENTS ============
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENT REQUIREMENTS', 15, yPos);
    yPos += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const requirements = [
      `Venue Type: ${this.formatVenueType(userParams.venueType)}`,
      `Dimensions: ${userParams.length}ft × ${userParams.width}ft × ${userParams.height}ft (${userParams.length * userParams.width} sq.ft)`,
      `Capacity: ${userParams.capacity} people`,
      `Primary Usage: ${userParams.usage.join(', ')}`,
      `Budget Range: ${this.formatBudget(userParams.budget)}`
    ];

    requirements.forEach(req => {
      doc.text(`• ${req}`, 20, yPos);
      yPos += 6;
    });

    yPos += 5;

    // ============ RECOMMENDED SYSTEM ============
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('RECOMMENDED SYSTEM', 15, yPos);
    yPos += 8;

    doc.setFontSize(11);
    doc.setTextColor(255, 107, 53);
    doc.text(recommendation.name, 15, yPos);
    yPos += 8;

    doc.setTextColor(0, 0, 0);

    // ============ EQUIPMENT TABLE ============
    const tableData = [];

    // Speakers
    recommendation.equipment.speakers.forEach(speaker => {
      tableData.push([
        `${speaker.brand} ${speaker.model}`,
        speaker.quantity,
        `₹${this.formatNumber(speaker.price)}`,
        `₹${this.formatNumber(speaker.subtotal)}`
      ]);
    });

    // Amplifiers
    recommendation.equipment.amplifiers.forEach(amp => {
      tableData.push([
        `${amp.brand} ${amp.model} Amplifier`,
        amp.quantity,
        `₹${this.formatNumber(amp.price)}`,
        `₹${this.formatNumber(amp.subtotal)}`
      ]);
    });

    // Mixer
    if (recommendation.equipment.mixer) {
      const mixer = recommendation.equipment.mixer;
      tableData.push([
        `${mixer.brand} ${mixer.model} Mixer`,
        1,
        `₹${this.formatNumber(mixer.price)}`,
        `₹${this.formatNumber(mixer.price)}`
      ]);
    }

    // Accessories
    recommendation.equipment.accessories.forEach(acc => {
      tableData.push([
        acc.description,
        acc.quantity,
        `₹${this.formatNumber(acc.price)}`,
        `₹${this.formatNumber(acc.subtotal)}`
      ]);
    });

    // Installation
    tableData.push([
      'Professional Installation & Setup',
      1,
      `₹${this.formatNumber(recommendation.pricing.installation)}`,
      `₹${this.formatNumber(recommendation.pricing.installation)}`
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Item Description', 'Qty', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [255, 107, 53], textColor: 255 },
      styles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 90 },
        1: { cellWidth: 20, halign: 'center' },
        2: { cellWidth: 35, halign: 'right' },
        3: { cellWidth: 35, halign: 'right' }
      }
    });

    yPos = doc.lastAutoTable.finalY + 5;

    // ============ PRICING SUMMARY ============
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    const subtotal = recommendation.pricing.subtotal + recommendation.pricing.installation;
    doc.text('SUBTOTAL:', pageWidth - 70, yPos);
    doc.text(`₹${this.formatNumber(subtotal)}`, pageWidth - 15, yPos, { align: 'right' });
    yPos += 6;

    doc.text('GST (18%):', pageWidth - 70, yPos);
    doc.text(`₹${this.formatNumber(recommendation.pricing.gst)}`, pageWidth - 15, yPos, { align: 'right' });
    yPos += 6;

    doc.setFontSize(12);
    doc.setTextColor(255, 107, 53);
    doc.text('TOTAL INVESTMENT:', pageWidth - 70, yPos);
    doc.text(`₹${this.formatNumber(recommendation.pricing.total)}`, pageWidth - 15, yPos, { align: 'right' });

    yPos += 12;

    // ============ WHY THIS SYSTEM ============
    if (yPos > pageHeight - 80) {
      doc.addPage();
      yPos = 20;
    }

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('WHY THIS SYSTEM:', 15, yPos);
    yPos += 6;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const splitText = doc.splitTextToSize(recommendation.whyRecommended, pageWidth - 30);
    doc.text(splitText, 15, yPos);
    yPos += splitText.length * 5 + 8;

    // ============ TECHNICAL SPECIFICATIONS ============
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('TECHNICAL SPECIFICATIONS:', 15, yPos);
    yPos += 6;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const specs = [
      `Coverage Area: ${recommendation.technicalSpecs.calculatedArea}`,
      `Target SPL: ${recommendation.technicalSpecs.targetSPL || recommendation.technicalSpecs.estimatedSPL}`,
      `Coverage Efficiency: ${recommendation.technicalSpecs.coverageEfficiency}`,
      `Power Density: ${recommendation.technicalSpecs.powerPerSqFt}`,
      `Frequency Response: ${recommendation.technicalSpecs.frequencyResponse || 'Full-range audio reproduction'}`
    ];

    specs.forEach(spec => {
      doc.text(`• ${spec}`, 20, yPos);
      yPos += 5;
    });

    yPos += 8;

    // ============ DISCLAIMERS ============
    doc.setFillColor(255, 243, 224);
    doc.rect(10, yPos, pageWidth - 20, 35, 'F');

    yPos += 7;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('⚠ IMPORTANT NOTES:', 15, yPos);
    yPos += 6;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const disclaimers = [
      '✓ This is a preliminary recommendation based on provided information',
      '✓ Final specifications may change after professional site inspection',
      '✓ System can be customized to match your exact budget and requirements',
      '✓ Installation includes acoustic tuning, testing, and user training',
      '✓ Quotation validity: 30 days from date of issue'
    ];

    disclaimers.forEach(disclaimer => {
      doc.text(disclaimer, 15, yPos);
      yPos += 4.5;
    });

    // ============ FOOTER ============
    doc.setFillColor(255, 107, 53);
    doc.rect(0, pageHeight - 25, pageWidth, 25, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('For Detailed Consultation & Site Visit:', pageWidth / 2, pageHeight - 17, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('📞 7287809847  |  📧 info@fhaudio.in  |  📍 Lalapeta, Guntur - 522003', 
      pageWidth / 2, pageHeight - 10, { align: 'center' });

    // ============ SAVE PDF ============
    const filename = `FH-Audio-Quotation-${quoteId}.pdf`;
    doc.save(filename);
  }

  /**
   * Helper: Format number with commas
   */
  static formatNumber(num) {
    return num.toLocaleString('en-IN');
  }

  /**
   * Helper: Format venue type
   */
  static formatVenueType(type) {
    const types = {
      'church': 'Church / Place of Worship',
      'auditorium': 'Auditorium / Theatre',
      'function_hall': 'Function Hall / Banquet',
      'corporate': 'Corporate / Meeting Room',
      'open_ground': 'Open Ground / Outdoor Event'
    };
    return types[type] || type;
  }

  /**
   * Helper: Format budget
   */
  static formatBudget(budget) {
    const budgets = {
      'economy': 'Economy (Budget-friendly)',
      'mid-range': 'Mid-range (Professional)',
      'premium': 'Premium (High-end)'
    };
    return budgets[budget] || budget;
  }
}

export default PDFGenerator;
